class QuickSearch extends HTMLElement {
    constructor() {
        super();

        this.quickSearch = this;
        this.headerInputs = [
            document.querySelector('#Search-In-Modal'), // Desktop
            document.querySelector('#search-form-mobile input[name="q"]') // Mobile
        ].filter(Boolean); // Filter out null inputs if one isn't present

        this.searchResults = this.querySelector('.quickSearchResultsContent');
        this.searchResultsWidget = this.querySelector('.quickSearchResultsWidget');
        this.productGrid = this.searchResults?.querySelector('.productGrid.products-grid');
        this.viewAllButton = this.searchResults?.querySelector('.productViewAll');
        this.maxDisplayCount = 4; // Limit to 4 products

        if (this.headerInputs.length === 0) {
            console.warn('No search inputs found for QuickSearch. Check your selectors.');
        } else {
            this.headerInputs.forEach((input) => {
                input.addEventListener('input', this.debounce(() => this.doQuickSearch(input), 300));
            });
        }
    }

    debounce(func, delay) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), delay);
        };
    }

    showLoadingSpinner() {
        const spinner = this.quickSearch.querySelector('.loading-overlay');
        if (spinner) spinner.style.display = 'flex';
    }

    hideLoadingSpinner() {
        const spinner = this.quickSearch.querySelector('.loading-overlay');
        if (spinner) spinner.style.display = 'none';
    }

    doQuickSearch(input) {
        const query = input.value.trim();

        // If the query is empty, revert to trending products
        if (!query) {
            this.displayTrendingProducts();
            return;
        }

        // Render placeholders
        if (this.productGrid) {
            this.productGrid.innerHTML = ''; // Clear existing products or placeholders
            for (let i = 0; i < this.maxDisplayCount; i++) {
                this.productGrid.innerHTML += `
                    <div class="product placeholder" style="min-height: 300px;">
                        <div class="product-image-placeholder"></div>
                        <div class="media__card__info">
                            <div class="product-title-placeholder"></div>
                            <div class="product-price-placeholder"></div>
                        </div>
                    </div>
                `;
            }
        }

        const url = `/search?view=ajax_quick_search&q=${query}*&type=product`;
        this.fetchSearchResults(url);
    }

    fetchSearchResults(url) {
        this.showLoadingSpinner(); // Show spinner

        fetch(url)
            .then((response) => response.json())
            .then((data) => {
                const products = data.results.slice(0, this.maxDisplayCount); // Ensure no more than 4 products are selected
                const count = parseInt(data.results_count.match(/\d+/)?.[0], 10) || 0;

                if (products.length > 0) {
                    this.displayResults(products, count, data.url);
                } else {
                    this.displayNoResults();
                }
            })
            .catch((error) => {
                console.error('Error fetching search results:', error);
                this.displayNoResults();
            })
            .finally(() => {
                this.hideLoadingSpinner(); // Hide spinner
            });
    }

    async fetchProductDetailsBatch(handles) {
        const promises = handles.slice(0, this.maxDisplayCount).map((handle) =>
            fetch(`${window.Shopify.routes.root}products/${handle}.js`)
                .then((response) => response.json())
                .catch(() => null)
        );
        return Promise.all(promises);
    }

    async displayResults(products, count, searchUrl) {
        if (!this.productGrid) return;

        // Clear the product grid and render results
        this.productGrid.innerHTML = '';
        let displayedCount = 0;

        const handles = products.map((product) => product.handle);
        const productDetails = await this.fetchProductDetailsBatch(handles);

        productDetails.forEach((product) => {
            if (product && displayedCount < this.maxDisplayCount) {
                displayedCount++;

                const isCoOrd = product.tags && product.tags.includes('co-ord');
                const coOrdHTML = isCoOrd ? `<div class="product__set__info">Co-Ord</div>` : '';

                const productHTML = `
                    <div class="product" style="min-height: 300px;">
                        <a class="pos__relative" href="${product.url}">
                            <img src="${product.featured_image || '/placeholder-image.jpg'}" alt="${product.title || 'No Title'}">
                            ${coOrdHTML}
                            <div class="media__card__info">
                                <div class="product-title">${product.title || 'No Title'}</div>
                                <div class="product-price">${(product.price / 100).toFixed(2)} INR</div>
                            </div>
                        </a>
                    </div>
                `;
                this.productGrid.innerHTML += productHTML;
            }
        });

        // "View All Results" button visibility
        if (this.viewAllButton) {
            this.viewAllButton.style.display = count > this.maxDisplayCount ? 'block' : 'none';
            this.viewAllButton.querySelector('a').setAttribute('href', searchUrl);
            this.viewAllButton.querySelector('a').innerText = `View All Results (${count})`;
        }

        this.searchResults.classList.remove('is-hidden');
        this.searchResults.classList.add('is-show');
        this.searchResultsWidget?.classList.remove('is-show');
        this.searchResultsWidget?.classList.add('is-hidden');
    }

    displayNoResults() {
        if (!this.productGrid) return;

        this.productGrid.innerHTML = '<p>No products found.</p>';
        if (this.viewAllButton) this.viewAllButton.style.display = 'none';

        this.searchResults.classList.remove('is-show');
        this.searchResults.classList.add('is-hidden');
        this.searchResultsWidget?.classList.remove('is-hidden');
    }

    displayTrendingProducts() {
        this.searchResults.classList.add('is-hidden');
        this.searchResults.classList.remove('is-show');
        this.searchResultsWidget?.classList.remove('is-hidden');
        this.searchResultsWidget?.classList.add('is-show');
    }
}

customElements.define('quick-search', QuickSearch);
